<template>
  <div class="vap-page">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>Amazon Bedrock Guardrails Management</h1>
      <div class="btns-container">
        <VasionButton classProp="secondary" @vasionButtonClicked="cancel()">
          Cancel
        </VasionButton>
        <VasionButton
          classProp="primary"
          :isDisabled="!(isDirty && formReadyToBeSaved)"
          @vasionButtonClicked="save()"
        >
          Save
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">
      <BedrockGuardrailForm
        ref="guardrailForm"
        :guardrailDataToEdit="guardrailDataToEdit"
        @markAsDirty="markAsDirty"
        @markFormStatus="markFormStatus"
        @markLoadingStatus="markLoadingStatus"
      />
    </div>
    <md-dialog
      id="modal-bedrock-guardrails-leave-confirmation"
      :md-active.sync="showLeaveDialog"
      :md-click-outside-to-close="false"
    >
      <VasionConfirmationDialog
        message="Are you sure you want to leave? Changes have not been saved."
        @noButtonClick="cancelLeave()"
        @yesButtonClick="doLeave()"
      />
    </md-dialog>
    <VasionSnackbar
      id="snackbar-bedrock-guardrails-management"
      ref="bedrockGuardrailsManagementSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>
<script>
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import Loading from 'vue-loading-overlay'
import BedrockGuardrailForm from '@/components/capture/BedrockGuardrailForm.vue'

export default {
  name: 'BedrockGuardrailsManagement',
  components: {
    BedrockGuardrailForm,
    Loading,
  },
  beforeRouteLeave(to, from, next) {
    if (this.$store.state.common.bypassNavigationGuards) return next()
    
    if (this.isDirty) {
      this.routeTo = to
      this.showLeaveDialog = true
    } else {
      this.routeTo = null
      next()
    }
  },
  data() {
    return {
      formReadyToBeSaved: false,
      guardrailDataToEdit: {},
      guardrailId: this.$route.params.guardrailId,
      isDirty: false,
      isLoading: false,
      loaderBackgroundColor,
      loaderColor,
      showLeaveDialog: false,
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
    }
  },
  computed: {
    isEdit() {
      return this.guardrailId ? true : false
    },
    isTextracLicenseActivated() {
      return this.$store.state.common.isTextractLicensed
    },
  },
  created: async function () {
    if (this.isTextracLicenseActivated === null) {
      await this.$store.dispatch('common/getIsTextractLicensed')
    }

    if (!this.isTextracLicenseActivated) {
      this.$router.push({ name: 'AdvancedImageProcessing' })
    }

    if (this.isEdit && this.guardrailId)
      await this.getSingleRegistry(this.guardrailId)
  },
  methods: {
    cancel() {
      if (this.isDirty) {
        this.routeTo = null
        this.showLeaveDialog = true
      } else {
        this.$router.push({ name: 'AdvancedImageProcessing' })
      }
    },
    cancelLeave() {
      this.showLeaveDialog = false
    },
    doLeave() {
      this.isDirty = false
      this.showLeaveDialog = false
      if (this.routeTo) {
        this.$router.push({ path: this.routeTo.path })
      } else {
        this.$router.push({ name: 'AdvancedImageProcessing' })
      }
    },
    async getSingleRegistry(id) {
      this.isLoading = true

      try {
        const response = await this.$store.dispatch('admin/getGuardrailAIP', id)
        if (response?.Data) {
          this.guardrailDataToEdit = response.Data
        } else if (response?.Value === 'False') {
          this.showSnackbar(
            'Amazon Bedrock Guardrail AIP',
            response.ResultMessage || 'An error occurred while fetching data',
            false
          )
        } else {
          this.showSnackbar(
            'Amazon Bedrock Guardrail AIP',
            'An error occurred while fetching data',
            false
          )
          setTimeout(() => {
            this.$router.push({ name: 'AdvancedImageProcessing' })
          }, 1500)
        }
      } catch (error) {
        this.showSnackbar(
          'Amazon Bedrock Guardrail AIP',
          'An unexpected error occurred',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    markAsDirty() {
      this.isDirty = true
    },
    markFormStatus(status) {
      this.formReadyToBeSaved = status
    },
    markLoadingStatus(status) {
      this.isLoading = status
    },
    async save() {
      if (!this.formReadyToBeSaved) return

      this.isLoading = true
      const {
        name,
        awsRegion,
        awsBucket,
        awsAccessKeyID,
        awsSecretKey,
        awsRoleArn,
        awsRoleDuration,
        awsRoleExternalId,
        guardrail,
        object,
        fieldId,
      } = this.$refs.guardrailForm.formFields
      const authMethod = this.$refs.guardrailForm.authMethod

      // Build AwsConfig based on auth method
      const awsConfig = {
        Region: awsRegion?.value,
        BucketName: awsBucket?.value,
      }
      
      if (authMethod === 'keySecret') {
        awsConfig.ClientID = awsAccessKeyID
        awsConfig.ClientSecret = awsSecretKey
      } else {
        awsConfig.RoleArn = awsRoleArn
        awsConfig.DurationSeconds = awsRoleDuration
        awsConfig.ExternalId = awsRoleExternalId
      }

      let payload = {
        Id: this.isEdit ? this.guardrailId : 0,
        Name: name,
        AwsConfig: awsConfig,
        IndexFormId: object?.value || 0,
        FieldId: fieldId?.id,
        GuardrailId: guardrail.value,
        GuardrailName: guardrail.displayName,
      }

      try {
        const response = await this.$store.dispatch(
          'admin/saveGuardrailAIP',
          payload
        )
        const success = response && response.Value === 'True'
        this.showSnackbar(
          'Amazon Bedrock Guardrail AIP',
          success
            ? 'Amazon Bedrock Guardrail AIP Successfully Saved'
            : response?.ResultMessage || 'An error occurred during save',
          success
        )
        if (success) {
          this.isDirty = false
          setTimeout(() => {
            this.$router.push({ name: 'AdvancedImageProcessing' })
          }, 2000)
        }
      } catch (error) {
        this.showSnackbar(
          'Amazon Bedrock Guardrail AIP',
          'An unexpected error occurred',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: isSuccess,
        showSnackbar: true,
      }
    },
  },
}
</script>
